import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { PieChart } from '@mui/x-charts/PieChart';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
    ArrowDown,
    ArrowUp,
    BarChart3,
    Calendar,
    Package,
    Repeat,
    Tag,
    Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { httpClient } from '../../common/httpclient';
import { FilterChips } from '../../component/filter/FilterToolbar';
import ReportFilterBar from '../../component/report/ReportFilterBar';
import DialogInfo from '../../component/dialog/DialogInfo';
import StatusChip from '../../component/common/StatusChip';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import '../reports/reports.css';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { parseMoney } from '../../component/common/MobileDashboardKit';
import { protoIcon } from '../../component/common/ProtoIcon';
import MobileReport, { MobileReportBody, formatMoneyKpi, useMobileRegenerate } from '../../component/report/MobileReport';
import { ChartCard, DonutBlock } from '../../component/report/MobileSalesSections';
import { MovementList, StockLevelBar } from '../../component/report/MobileStockSections';
import { KitSeg } from '../../component/common/MobileKitExtras';
import KpiTickerCard from '../../component/report/KpiTickerCard';


// DESIGN-SYSTEM.md §5.11 empty state (variante compacta, dentro de um
// painel de gráfico) — mesmo componente local já usado nos outros relatórios.
function EmptyMiniState({ icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3, textAlign: 'center' }}>
            <Box sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
            }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
    );
}

// `.legend-item` — dot + label + valor em negrito, usado sob os donuts.
function LegendRow({ color, label, value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '11.5px', color: 'text.secondary', width: '100%' }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
            <Typography component="span" sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
            <Typography component="span" sx={{ fontSize: '11.5px', fontWeight: 700, color: 'text.primary', ml: 'auto' }}>{value}</Typography>
        </Box>
    );
}

// Painel de gráfico genérico (`.panel-head`) — mesmo componente local já
// usado nos outros relatórios.
function ChartPanel({ title, subtitle, loading, children }) {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 2 }}>
                    {loading ? (
                        <>
                            <Skeleton variant="text" width={160} height={20} />
                            <Skeleton variant="text" width={200} height={14} />
                        </>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
                            {subtitle ? (
                                <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>{subtitle}</Typography>
                            ) : null}
                        </>
                    )}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    {children}
                </Box>
            </CardContent>
        </Card>
    );
}

// Donut (PieChart real via @mui/x-charts, DESIGN-SYSTEM.md §5.9 — nunca
// conic-gradient à mão) com overlay central + legenda em linhas.
function DonutPanel({ title, subtitle, data, centerValue, centerCaption, loading, emptyTitle, emptySubtitle }) {
    const hasData = data.some((item) => item.value > 0);
    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 1 }}>
                    <Skeleton variant="circular" width={128} height={128} />
                    <Skeleton variant="text" width="80%" height={16} />
                    <Skeleton variant="text" width="70%" height={16} />
                </Box>
            ) : hasData ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ position: 'relative', width: 128, height: 128 }}>
                        <PieChart
                            series={[{
                                data,
                                innerRadius: 38,
                                outerRadius: 64,
                                paddingAngle: 2,
                                cornerRadius: 3,
                            }]}
                            height={128}
                            width={128}
                            slotProps={{ legend: { hidden: true } }}
                            tooltip={{ trigger: 'item' }}
                            sx={{ '& .MuiChartsLegend-root': { display: 'none' } }}
                        />
                        <Box sx={{
                            position: 'absolute',
                            inset: 18,
                            borderRadius: '50%',
                            bgcolor: 'background.paper',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}>
                            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: 'text.primary', lineHeight: 1.1 }}>
                                {centerValue}
                            </Typography>
                            <Typography sx={{ fontSize: '9.5px', color: 'text.disabled' }}>{centerCaption}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
                        {data.map((item, index) => (
                            <LegendRow key={`${item.label}-${index}`} color={item.color} label={item.label} value={item.displayValue ?? item.value} />
                        ))}
                    </Box>
                </Box>
            ) : (
                <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title={emptyTitle} subtitle={emptySubtitle} />
            )}
        </ChartPanel>
    );
}

/**
 * Barra de 3 zonas (baixo/médio/alto) com marcador — DESIGN-SYSTEM.md §5.12
 * "3-zone level bar (stock level)", mesmo padrão já usado por
 * `pages/products/Reposition.js` (`StockLevelBar`), reproduzido aqui com as
 * legendas Baixo/Médio/Alto e o texto de apoio do protótipo
 * (`prototipo-relatorios.html` `.stock-level`/`.stock-level-labels`/`.hint`).
 */
function StockLevelPanel({ value, loading }) {
    const level = Math.max(0, Math.min(100, Number(value) || 0));
    const band = level <= 20 ? 'Baixa' : level <= 55 ? 'Média' : 'Alta';

    return (
        <ChartPanel title="Nível de estoque" subtitle="Posição atual x zonas de referência" loading={loading}>
            {loading ? (
                <Skeleton variant="rounded" width="100%" height={54} />
            ) : (
                <Box>
                    <Box sx={{ height: 14, borderRadius: '8px', overflow: 'hidden', display: 'flex', backgroundColor: 'background.subtle', position: 'relative' }}>
                        <Box sx={{ width: '20%', backgroundColor: 'error.bg' }} />
                        <Box sx={{ width: '35%', backgroundColor: 'warning.bg' }} />
                        <Box sx={{ width: '45%', backgroundColor: 'success.bg' }} />
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -5,
                                width: 4,
                                height: 24,
                                borderRadius: '3px',
                                backgroundColor: 'text.primary',
                                boxShadow: (t) => `0 0 0 3px ${t.palette.background.paper}`,
                                left: `${level}%`,
                                transition: 'left .25s ease',
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '10.5px', color: 'text.disabled', fontWeight: 600, mt: '6px' }}>
                        <span>Baixo</span>
                        <span>Médio</span>
                        <span>Alto</span>
                    </Box>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 1 }}>
                        Estoque atual está na faixa <Typography component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{band}</Typography> — {level.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% da capacidade de referência.
                    </Typography>
                </Box>
            )}
        </ChartPanel>
    );
}

export default function StockReport() {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    // Tendência dos KPIs (`POST /chart/stock/kpi-trend`); falha não derruba o relatório.
    const [kpiTrend, setKpiTrend] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs().endOf('month'));
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');

    const fetchReportData = async () => {
        if (!startDate || !endDate) {
            setDialogMessage('Por favor, selecione as datas de início e fim.');
            setOpenDialog(true);
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                start: startDate.format('DD/MM/YYYY'),
                end: endDate.format('DD/MM/YYYY'),
            });
            const trendRequest = httpClient.post('/chart/stock/kpi-trend', {
                start: queryParams.get('start'),
                end: queryParams.get('end'),
            })
                .then((res) => res.data)
                .catch(() => null);
            const response = await httpClient.post(`/chart/stock?${queryParams.toString()}`);
            setKpiTrend(await trendRequest);
            setReportData(response.data);
        } catch (error) {
            setDialogMessage('Erro ao gerar relatório. Tente novamente.');
            setOpenDialog(true);
        } finally {
            setLoading(false);
        }
    };

    // Filtros (mobile): volta ao período padrão sem gerar o relatório.
    const handleClearFilters = () => {
        setStartDate(dayjs().startOf('month'));
        setEndDate(dayjs().endOf('month'));
    };

    const handleExport = async (type) => {
        if (!startDate || !endDate) {
            setDialogMessage('Por favor, selecione as datas de início e fim.');
            setOpenDialog(true);
            return;
        }

        const start = startDate.format('DD/MM/YYYY');
        const end = endDate.format('DD/MM/YYYY');
        const queryParams = new URLSearchParams({ start, end });

        try {
            if (type === 'stock') {
                await httpClient.downloadFile(`/chart/stock/export?${queryParams.toString()}`, {
                    title: `estoque_${start}_${end}`,
                    extension: 'csv',
                });
            } else {
                await httpClient.downloadFile(`/chart/stock/export-moviments?${queryParams.toString()}`, {
                    title: `movimentacoes_estoque_${start}_${end}`,
                    extension: 'csv',
                });
            }
        } catch (error) {
            setDialogMessage('Erro ao baixar arquivo.');
            setOpenDialog(true);
        }
    };

    // `StockMovimentsResponse` (report/stock.go) não traz um `id` por linha —
    // `DefaultTable` usa `row.id` como `key` do React; sem isso toda linha
    // teria `key={undefined}` (aviso de "unique key prop" no console, mesmo
    // problema pré-existente na versão anterior desta tela). Índice como
    // fallback é seguro aqui: a lista é somente leitura (sem reordenação/
    // edição inline que dependeria de um id estável).
    const moviments = (reportData?.StockMovimentsResponse || []).map((m, index) => ({ ...m, id: m.id ?? index }));

    const inOutDonutData = reportData?.ReportIntOut ? [
        {
            id: 0,
            value: reportData.ReportIntOut.CountIn || 0,
            label: 'Entradas',
            color: theme.palette.success.main,
        },
        {
            id: 1,
            value: reportData.ReportIntOut.CountOut || 0,
            label: 'Saídas',
            color: theme.palette.error.main,
        },
    ] : [];
    const totalInOut = inOutDonutData.reduce((sum, item) => sum + item.value, 0);
    const inPct = totalInOut > 0 ? Math.round(((reportData?.ReportIntOut?.CountIn || 0) / totalInOut) * 100) : 0;

    const isMobileShell = useIsMobileShell();
    // Mobile: gera ao abrir e a cada troca de período/filtros (mesma chamada e payload do botão "Gerar").
    const regenerate = useMobileRegenerate(fetchReportData, { enabled: isMobileShell });

    if (isMobileShell) {
        const download = protoIcon('download');
        const moneyKpi = (raw) => {
            const n = parseMoney(raw);
            return Math.abs(n) >= 100000 ? formatMoneyKpi(n) : `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        };

        // Lista: mesma ordem padrão do desktop (data da movimentação, decrescente).
        const sortedMoviments = [...moviments].sort((a, b) => {
            const av = String(a.moviment_date ?? '').toLowerCase();
            const bv = String(b.moviment_date ?? '').toLowerCase();
            return av > bv ? -1 : av < bv ? 1 : 0;
        });
        const getListRow = (row) => {
            // Mesmo sinal do desktop: só Entrada/Saída têm direção inequívoca.
            const type = (row.type_moviment || '').toLowerCase();
            const qty = Number(row.quantity || 0).toLocaleString('pt-BR');
            let quantity = qty;
            let quantityTone;
            if (type === 'entrada') { quantity = `+${qty}`; quantityTone = 'success'; }
            else if (type === 'saída' || type === 'saida') { quantity = `−${qty}`; quantityTone = 'danger'; }
            return {
                product: row.product_name,
                quantity,
                quantityTone,
                type: row.type_moviment,
                date: row.moviment_date ? dayjs(row.moviment_date).format('DD/MM, HH:mm') : '—',
                user: row.user,
                observations: row.observations,
            };
        };

        const chartsNode = (
            <>
                <ChartCard title="Nível de estoque" subtitle="Posição atual x zonas de referência">
                    <StockLevelBar value={reportData?.NivelStock} />
                </ChartCard>
                <ChartCard title="Entrada x saída de itens" bare>
                    <DonutBlock
                        segments={inOutDonutData.map((item) => ({ value: item.value, color: item.color }))}
                        center={`${inPct}%`}
                        legend={inOutDonutData.map((item) => ({ label: `${item.label} · ${item.value.toLocaleString('pt-BR')}`, color: item.color }))}
                        empty="Sem entradas ou saídas no período selecionado"
                    />
                </ChartCard>
            </>
        );

        const tabsNode = (
            <Box sx={{ mt: '22px' }}>
                <KitSeg
                    value={activeTab}
                    onChange={setActiveTab}
                    options={[{ value: 0, label: 'Movimentações' }, { value: 1, label: 'Gráficos' }]}
                />
                <Box sx={{ mt: '14px' }}>
                    {activeTab === 0 ? (
                        <MovementList key={sortedMoviments.length} rows={sortedMoviments} getRow={getListRow} />
                    ) : chartsNode}
                </Box>
            </Box>
        );

        return (
            <PageScaffold title="Relatório de Estoque" titlePageTarget="Estoque" links={[{ title: 'Relatórios', link: '/reports' }]}>
                <MobileReport
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    onGenerate={regenerate}
                    onClear={handleClearFilters}
                    exportItems={[
                        { label: 'Excel (CSV) · Estoque', icon: download, onClick: () => handleExport('stock') },
                        { label: 'Excel (CSV) · Movimentações', icon: download, onClick: () => handleExport('movements') },
                    ]}
                >
                    <MobileReportBody
                        loading={loading}
                        ready={Boolean(reportData)}
                        kpis={[
                            { label: 'Total de itens', icon: 'package', value: Number(reportData?.CountStock || 0).toLocaleString('pt-BR') },
                            { label: 'Valor do estoque', icon: 'dollar', grad: true, value: moneyKpi(reportData?.TotalAmountStock || '0,00') },
                            { label: 'Custo do estoque', icon: 'tag', value: moneyKpi(reportData?.TotalAmountCostStock || '0,00') },
                            { label: 'Movimentações', icon: 'layers', value: moviments.length.toLocaleString('pt-BR') },
                        ]}
                        sections={[
                            { type: 'node', title: 'Detalhamento', node: tabsNode },
                        ]}
                    />
                </MobileReport>
                <DialogInfo open={openDialog} onClose={() => setOpenDialog(false)} title="Erro" iconType="error" content={dialogMessage} />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            title="Relatório de Estoque"
            titlePageTarget="Estoque"
            links={[{ title: "Relatórios", link: "/reports" }]}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                    {/* page-head — ícone + título + descrição + duas ações de export
                        (estoque / movimentações), igual a prototipo-relatorios.html. */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'flex-end' },
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 2.5,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                width: 38,
                                height: 38,
                                borderRadius: '11px',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                background: (t) => t.palette.gradient.brand,
                            }}>
                                <Package size={18} strokeWidth={2} />
                            </Box>
                            <Box>
                                <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                    Relatório de Estoque
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                    Valor em estoque e movimentações (entradas, saídas, estornos).
                                </Typography>
                            </Box>
                        </Box>

                    </Box>

                    {/* filter-bar (sem card) — período + ações (Limpar / Exportar / Gerar). */}
                    <ReportFilterBar
                        start={startDate}
                        end={endDate}
                        onPeriodChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                        onGenerate={fetchReportData}
                        loading={loading}
                        onClear={handleClearFilters}
                        exportItems={[
                            { label: 'Estoque', description: 'Posição de estoque do período (CSV)', onClick: () => handleExport('stock') },
                            { label: 'Movimentações', description: 'Entradas, saídas e estornos (CSV)', onClick: () => handleExport('movements') },
                        ]}
                    />

                    {/* Chips de filtro ativo + contagem no período. */}
                    <FilterChips sx={{ mb: { xs: 1.5, sm: 3 } }}>
                        <Chip
                            icon={<Calendar size={13} strokeWidth={2} />}
                            label={`Período: ${startDate?.format('DD/MM/YYYY')} – ${endDate?.format('DD/MM/YYYY')}`}
                            size="small"
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                        {reportData && (
                            <Chip
                                icon={<Repeat size={13} strokeWidth={2} />}
                                label={`${moviments.length} ${moviments.length === 1 ? 'movimentação' : 'movimentações'} no período`}
                                size="small"
                                sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                            />
                        )}
                    </FilterChips>

                    {/* Conteúdo do relatório */}
                    {reportData ? (
                        <>
                            {/* KPIs estilo bolsa de valores (`KpiTickerCard`), 5 cards —
                                ref. `ui-ux/prototipo-relatorio-estoque-kpis.html` (que ainda
                                mostra 8). Grid de 30 colunas: 2+2+1 em tela estreita, 3+2 em md
                                (linha de baixo esticada) e 5 em xl, sem slots vazios. Valor/custo/itens
                                e entradas são neutros; sem a tendência (falha do endpoint) os
                                cards mostram só os valores atuais de `/chart/stock`. */}
                            <Box sx={{
                                display: 'grid',
                                // 30 colunas (mmc de 2, 3 e 5): cada breakpoint fecha a linha sem sobra.
                                // xs/sm: 2+2+1 (último esticado); md/lg: 3+2 (os 2 de baixo esticados);
                                // xl: 5 numa linha.
                                gridTemplateColumns: 'repeat(30, minmax(0, 1fr))',
                                gap: { xs: 1, md: '10px' },
                                mb: '14px',
                                '& > *': { gridColumn: { xs: 'span 15', md: 'span 10', xl: 'span 6' }, minWidth: 0 },
                                '& > :nth-of-type(n+4)': { gridColumn: { md: 'span 15', xl: 'span 6' } },
                                '& > :last-child': { gridColumn: { xs: 'span 30', md: 'span 15', xl: 'span 6' } },
                            }}>
                                <KpiTickerCard uid="val" kind="money" neutral hideSpark estimated estimatedHint="Estimado: série reconstruída a partir das movimentações, com o preço de venda atual" label="Valor do estoque" icon={<Wallet size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" data={kpiTrend?.value} days={kpiTrend?.days} value={Math.round(parseMoney(reportData.TotalAmountStock) * 100)} loading={loading} />
                                <KpiTickerCard uid="cst" kind="money" neutral hideSpark estimated estimatedHint="Estimado: série reconstruída a partir das movimentações, com o custo atual" label="Custo do estoque" icon={<Tag size={11} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" data={kpiTrend?.cost} days={kpiTrend?.days} value={Math.round(parseMoney(reportData.TotalAmountCostStock) * 100)} loading={loading} />
                                <KpiTickerCard uid="uni" kind="units" neutral hideSpark label="Itens em estoque (un.)" icon={<Package size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" data={kpiTrend?.units} days={kpiTrend?.days} value={Number(reportData.CountStock || 0)} loading={loading} />
                                <KpiTickerCard uid="ent" kind="units" neutral cumulative label="Entradas (un.)" icon={<ArrowUp size={11} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" data={kpiTrend?.entries} days={kpiTrend?.days} value={Number(reportData.ReportIntOut?.CountIn || 0)} loading={loading} />
                                <KpiTickerCard uid="sai" kind="units" cumulative label="Saídas (un.)" icon={<ArrowDown size={11} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main" data={kpiTrend?.exits} days={kpiTrend?.days} value={Number(reportData.ReportIntOut?.CountOut || 0)} loading={loading} />
                            </Box>

                            {/* seg-tabs — Movimentações / Gráficos, igual a prototipo-relatorios.html. */}
                            <Box sx={{ mb: 2.25 }}>
                                <ToggleButtonGroupTabs activeTab={activeTab} onChange={setActiveTab} />
                            </Box>

                            {activeTab === 0 && (
                                <Box>
                                    <DefaultTable
                                        colunms={[
                                            { path: 'product_name', name: 'Produto' },
                                            {
                                                path: 'quantity',
                                                name: 'Quantidade',
                                                render: (row) => {
                                                    // Sinal de +/- só é aplicado para Entrada/Saída, cuja direção
                                                    // é inequívoca (mesmo mapeamento de ícone up/down de
                                                    // `StatusChip domain="stockMovement"`, DESIGN-SYSTEM.md §4).
                                                    // "Cancelado"/"Estorno de Venda" sempre *devolvem* item ao
                                                    // estoque no backend atual (`sale/repository.go` `cancel`/
                                                    // `reverseTransaction`, `serviceorder/repository.go`
                                                    // `cancelItemProduct`) — diferente do protótipo estático, que
                                                    // mostra esses dois tipos com sinal negativo só como mockup
                                                    // visual. Para não afirmar uma direção que a API não garante,
                                                    // esses dois tipos ficam sem sinal.
                                                    const type = (row.type_moviment || '').toLowerCase();
                                                    const qty = Number(row.quantity || 0).toLocaleString('pt-BR');
                                                    if (type === 'entrada') return `+${qty}`;
                                                    if (type === 'saída' || type === 'saida') return `−${qty}`;
                                                    return qty;
                                                },
                                            },
                                            {
                                                path: 'type_moviment',
                                                name: 'Movimento',
                                                render: (row) => <StatusChip domain="stockMovement" value={row.type_moviment} />,
                                            },
                                            {
                                                path: 'moviment_date',
                                                name: 'Data',
                                                render: (row) => (
                                                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                                                        {row.moviment_date ? dayjs(row.moviment_date).format('DD/MM, HH:mm') : '—'}
                                                    </Typography>
                                                ),
                                            },
                                            { path: 'user', name: 'Usuário' },
                                            { path: 'observations', name: 'Observações' },
                                        ]}
                                        data={moviments}
                                        rowPerPage={10}
                                        itemLabel="movimentações"
                                        sortableFields={['product_name', 'moviment_date']}
                                        defaultSort={{ field: 'moviment_date', direction: 'desc' }}
                                    />
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <StockLevelPanel value={reportData.NivelStock} loading={loading} />

                                    {/* charts-grid — Entrada x saída / Produtos com estoque baixo
                                        (prototipo-relatorios.html). O 2º painel não tem dado
                                        equivalente em `/chart/stock` — ver comentário de cobertura
                                        no topo do arquivo — e fica com um estado vazio explicativo. */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                                        <DonutPanel
                                            title="Entrada x saída de itens"
                                            data={inOutDonutData}
                                            centerValue={`${inPct}%`}
                                            centerCaption="entrada"
                                            loading={loading}
                                            emptyTitle="Nenhuma movimentação"
                                            emptySubtitle="Sem entradas ou saídas no período selecionado"
                                        />
                                        <ChartPanel title="Produtos com estoque baixo" loading={loading}>
                                            <EmptyMiniState
                                                icon={<Package size={22} strokeWidth={2} />}
                                                title="Indisponível neste relatório"
                                                subtitle="Este indicador (produto x estoque mínimo) faz parte do Relatório de Produtos"
                                            />
                                        </ChartPanel>
                                    </Box>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Card>
                            <CardContent sx={{ py: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: '50%',
                                    border: '2px dashed',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'text.disabled',
                                }}>
                                    <Package size={30} strokeWidth={2} />
                                </Box>
                                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.secondary' }}>
                                    Nenhum relatório gerado
                                </Typography>
                                <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', maxWidth: 380 }}>
                                    Selecione um período e clique em "Gerar relatório" para visualizar os dados de estoque.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </LocalizationProvider>

            <DialogInfo
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                title="Erro"
                iconType="error"
                content={dialogMessage}
            />
        </PageScaffold>
    );
}

// seg-tabs — `ToggleButtonGroup` restyled globalmente em `theme.js`, com as
// medidas próprias do protótipo (mesma sobrescrita local de
// `SalesReport.js`/`ServiceOrderReport.js`).
const segTabButtonSx = {
    gap: '7px',
    padding: '9px 18px',
    lineHeight: 'normal',
    borderRadius: '8px !important',
    fontSize: '12.5px',
    fontWeight: 700,
};

function ToggleButtonGroupTabs({ activeTab, onChange }) {
    return (
        <ToggleButtonGroup
            exclusive
            value={activeTab}
            onChange={(e, v) => (v || v === 0) && onChange(v)}
            sx={{ width: 'fit-content', p: '4px', gap: '4px', borderRadius: '11px' }}
        >
            <ToggleButton value={0} sx={segTabButtonSx}>
                <Repeat size={14} strokeWidth={2} /> Movimentações
            </ToggleButton>
            <ToggleButton value={1} sx={segTabButtonSx}>
                <BarChart3 size={14} strokeWidth={2} /> Gráficos
            </ToggleButton>
        </ToggleButtonGroup>
    );
}
